import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CODES } from "../../consts/codes";
import { GetActiveProduct, getCompanyByEan } from "../../services/admin/adminServices";

/** Slice de Redux para el estado de listado de usuarios. */
export const listUsersSlice = createSlice({
  name: "listUsers",
  initialState: {
    products: [],
    productsStatus: "fetch",
    error: null,
    productsAndRoles: [],
    productsAndRolesStatus: "fetch",
    productsAllAndRoles: [],
    productsAllAndRolesStatus: "fetch",
    errorRoles: null,
    currentCompany: {
      companyName: "",
      eanCompany: "",
      country: "",
      typeCompany: {
        value: "",
        label: "",
      },
    },
    currentCompanyStatus: "fetch",
    errorCompany: null,
    dataKeyFilters: {
      nameCompany: "",
      eanCompany: "",
      country: {
        country: "",
        countryName: "",
      },
      product: "",
      typeCompany: {
        value: "",
        label: "",
      },
    },
    dataKeyFiltersStatus: "fetch",
    errorDataKey: null,
  },
  reducers: {
    /** Reducer para establecer la compañía actual. */
    setCurrentCompany(state, action) {
      const { company, typeCompany } = action.payload;
      const modifiedCompany = {
        ...company,
        companyName: company.name,
        typeCompany: typeCompany,
      };
      state.currentCompany = modifiedCompany;
      state.currentCompanyStatus = "succeeded";
    },

    /** Reducer para reiniciar la compañía actual a su estado inicial. */
    resetCurrentCompany(state, action) {
      state.currentCompany = {
        companyName: "",
        eanCompany: "",
        country: "",
        typeCompany: {},
      };
      state.currentCompanyStatus = "clean";
    },

    /** Reducer para establecer los filtros de clave de datos. */
    setDataKeyFilters(state, action) {
      const { datakey } = action.payload;
      state.dataKeyFilters = datakey;
      state.dataKeyFiltersStatus = "succeeded";
    },

    /** Reducer para reiniciar el estado de productos y roles. */
    resetProductsAndRolesStatus(state, action) {
      state.productsAndRolesStatus = "fetch";
    },
    /** Reducer para reiniciar el estado de todos los productos y roles. */
    resetProductsAllAndRolesStatus(state, action) {
      state.productsAllAndRolesStatus = "fetch";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getCompanyByEanCompany.pending, (state, action) => {
        state.currentCompanyStatus = "loading";
        state.dataKeyFiltersStatus = "loading";
      })
      .addCase(getCompanyByEanCompany.fulfilled, (state, action) => {
        state.productsStatus = "succeeded";
        const payload = action.payload;
        state.currentCompany = payload;
        const keyFilters = {
          country: payload?.country
            ? payload.country
            : {
                country: "",
                countryName: "",
              },
          nameCompany: payload?.companyName ? payload.companyName : "",
          eanCompany: payload?.eanCompany ? payload.eanCompany : "",
          product: "",
          typeCompany: payload?.typeCompany
            ? payload.typeCompany
            : {
                value: "",
                label: "",
              },
          navigateUserDetalis: false,
        };
        state.dataKeyFilters = keyFilters;
      })
      .addCase(getCompanyByEanCompany.rejected, (state, action) => {
        state.currentCompanyStatus = "failed";
        state.dataKeyFiltersStatus = "failed";
        state.errorDataKey = action.error.message;
        state.errorCompany = action.error.message;
      })
      .addCase(fetchProducts.pending, (state, action) => {
        state.productsStatus = "loading";
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.productsStatus = "succeeded";
        // Add any fetched posts to the array
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.productsStatus = "failed";
        state.error = action.error.message;
      });
  },
});

/** Selector para obtener la lista de productos. */
export const getProducts = (state) => state.listUsers.products;

/** Selector para obtener el estado de la petición de productos. */
export const getProductsStatus = (state) => state.listUsers.productsStatus;

/** Selector para obtener la lista de productos y roles. */
export const getProductsAndRoles = (state) => state.listUsers.productsAndRoles;

/** Selector para obtener el estado de la petición de productos y roles. */
export const getProductsAndRolesStatus = (state) => state.listUsers.productsAndRolesStatus;

/** Selector para obtener la lista de todos los productos y roles. */
export const getProductsAllAndRoles = (state) => state.listUsers.productsAllAndRoles;

/** Selector para obtener el estado de la petición de todos los productos y roles. */
export const getProductsAllAndRolesStatus = (state) => state.listUsers.productsAllAndRolesStatus;

/** Selector para obtener la compañía actual. */
export const getCurrentCompany = (state) => state.listUsers.currentCompany;

/** Selector para obtener el estado de la petición de la compañía actual. */
export const getCurrentCompanyStatus = (state) => state.listUsers.currentCompanyStatus;

/** Selector para obtener los filtros de clave de datos. */
export const getDataKeyFilters = (state) => state.listUsers.dataKeyFilters;

/** Selector para obtener el estado de la petición de filtros de clave de datos. */
export const getDataKeyFiltersStatus = (state) => state.listUsers.dataKeyFiltersStatus;

export const {
  setCurrentCompany,
  resetProductsAndRolesStatus,
  resetProductsAllAndRolesStatus,
  setDataKeyFilters,
  resetCurrentCompany,
} = listUsersSlice.actions;

/** AsyncThunk para obtener la compañía por código EAN y país. */
export const getCompanyByEanCompany = createAsyncThunk(
  "listUsers/currentCompany",
  async ({ eanCompany, country }) => {
    const getCompany = await getCompanyByEan({
      eanCompany: eanCompany,
      country: country,
    });

    if (getCompany.status === CODES.COD_RESPONSE_HTTP_OK) {
      if (
        getCompany.data.responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST ||
        getCompany.data.responseCode === CODES.COD_RESPONSE_SUCCESS
      ) {
        return getCompany.data.responseMessage;
      }
    }
  },
);

/** AsyncThunk para obtener la lista de productos activos. */
export const fetchProducts = createAsyncThunk(
  "listUsers/products",
  async ({ eanCompany, country }) => {
    const productsService = await GetActiveProduct({
      eanCompany: eanCompany,
      country: country,
    });
    if (productsService.status === CODES.COD_RESPONSE_HTTP_OK) {
      if (
        productsService.data.responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST ||
        productsService.data.responseCode === CODES.COD_RESPONSE_SUCCESS
      ) {
        return productsService.data.responseMessage;
      }
    }
  },
);

/** Reducer del slice listUsers */
export default listUsersSlice.reducer;